import {List, type Map} from 'immutable';

import createSelector from 'web-app/util/custom-create-selector';
import * as ChildrenSelectors from 'web-app/react/entities/children/selectors';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import BillPayerBehaviors from 'web-app/behaviors/bill-payer-behavior-ids';
import {type RootState} from 'web-app/react/redux/root-state';

import {type BillPayer} from './model';

export const getBillPayers = (state: RootState): Map<string, BillPayer> => state.entities.billPayers;

export const getBillPayersByInstitutionId = createSelector(
    getBillPayers,
    (_, props: {institutionId: string}) => props.institutionId,
    (billPayers, institutionId) => {
        return billPayers.filter(billPayer => billPayer.institutionId === institutionId);
    },
);

export const getChildBillPayers = createSelector(
    ChildrenSelectors.getChildByProps,
    getBillPayers,
    (child, billPayers): List<BillPayer> =>
        child ? child.billPayerIds.map(id => billPayers.get(id)) : List<BillPayer>(),
);

// used createSelector generic to correct return value - billPayer map can be empty - resulting in returned value being undefined
export const getFirstBillPayer = createSelector<RootState, Map<string, BillPayer>, BillPayer | undefined>(
    getBillPayers,
    billPayers => {
        const [billPayer] = billPayers.values();
        return billPayer;
    },
);

export const getBillPayerByProps = createSelector(
    getBillPayers,
    (state: RootState, props: {billPayerId: string}) => props.billPayerId,
    (billPayers, billPayerId) => {
        return billPayers.get(billPayerId);
    },
);

export const getBillPayerBalanceBehaviors = createSelector(getBillPayerByProps, billPayer =>
    billPayer ? billPayer.balanceBehaviors : new BehaviorsCollection([]),
);

export const getBillPayerCreditBehaviors = createSelector(getBillPayerByProps, billPayer =>
    billPayer ? billPayer.creditBehaviors : new BehaviorsCollection([]),
);

export const getBillPayerSurchargeBehaviors = createSelector(getBillPayerByProps, billPayer =>
    billPayer ? billPayer.surchargeBehaviors : new BehaviorsCollection([]),
);

export const expectedIds = createSelector(getBillPayerByProps, billPayer => (billPayer ? billPayer.expectedIds : []));
export const unexpectedIds = createSelector(getBillPayerByProps, billPayer =>
    billPayer ? billPayer.unexpectedIds : [],
);

export const childBillPayersWithShowBalance = createSelector(
    ChildrenSelectors.getChildByProps,
    getBillPayers,
    (child, billPayers) => {
        if (child) {
            return child.billPayerIds
                .map(billPayerId => billPayers.get(billPayerId))
                .filter(billPayer => billPayer.behaviors.includes(BillPayerBehaviors.ShowBillPayerBalance));
        } else {
            return List();
        }
    },
);

export const showBillPayerBankDetails = createSelector(getBillPayerByProps, billPayer => {
    if (!billPayer || !billPayer.behaviors) {
        return false;
    }

    return billPayer.behaviors.behaviorForId(BillPayerBehaviors.ShowBillPayerBankDetails);
});
